import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import type { RadioShow } from '@/types';

export type AudioSourceType = 'stream' | 'podcast' | null;

export type PodcastQueueItem = {
  audioUrl: string;
  title: string;
  imageUrl?: string | null;
};

export type PodcastPlayOptions = {
  imageUrl?: string | null;
  queue?: PodcastQueueItem[];
};

interface AudioContextState {
  // Source type
  sourceType: AudioSourceType;
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  
  // Stream-specific
  streamUrl: string | null;
  nowPlaying: RadioShow | null;
  isLive: boolean;
  streamNowPlayingText: string | null;
  streamTrack: {
    title: string;
    artist: string;
    artwork: string;
    artUrl: string;
    album: string;
    duration: number;
    isOnline: boolean;
    nextText: string;
    stationId: number;
    shortcode: string;
    stationName: string;
    listenUrl: string;
    text: string;
    elapsed: number;
    fetchedAt: number;
    listeners: number;
  } | null;
  
  // Podcast-specific
  currentEpisode: PodcastQueueItem | null;
  currentTime: number;
  duration: number;
  isLoading: boolean;
  error: string | null;
  hasPreviousEpisode: boolean;
  hasNextEpisode: boolean;
  
  // Actions
  playStream: () => void;
  playPodcast: (audioUrl: string, title: string, options?: PodcastPlayOptions) => void;
  pause: () => void;
  toggle: () => void;
  setVolume: (v: number) => void;
  toggleMute: () => void;
  seek: (time: number) => void;
  skipBy: (seconds: number) => void;
  playNextEpisode: () => void;
  playPreviousEpisode: () => void;
  clearError: () => void;
  setStreamTrack: (track: {
    title: string;
    artist: string;
    artwork: string;
    artUrl: string;
    album: string;
    duration: number;
    isOnline: boolean;
    nextText: string;
    stationId: number;
    shortcode: string;
    stationName: string;
    listenUrl: string;
    text: string;
    elapsed: number;
    fetchedAt: number;
    listeners: number;
  } | null) => void;
}

const AudioContext = createContext<AudioContextState | null>(null);

const DEFAULT_STREAM_URL = 'https://radio.gayphx.com/listen/gayphx/radio.mp3';
const ICECAST_STATUS_URL = 'https://radio.gayphx.com/listen/gayphx/status-json.xsl';
const RADIO_NOW_PLAYING_URL = 'https://radio.gayphx.com/api/nowplaying/gayphx';
const VOLUME_STORAGE_KEY = 'gayphx-player-volume';
const NOW_PLAYING_POLL_MS = 8_000;
const LIVE_BREAK_LABEL = 'LIVE BREAK';

function asTrimmedText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

async function playNow(audio: HTMLAudioElement): Promise<void> {
  try {
    await audio.play();
  } catch (err) {
    if (!isAbortError(err)) throw err;
    await new Promise((resolve) => window.setTimeout(resolve, 50));
    await audio.play();
  }
}

function isHiddenProductionLabel(value: string): boolean {
  const label = value.replace(/[\s._/\\-]+/g, ' ').trim().toLowerCase();
  if (!label || label === 'station offline') return false;
  if (/^vt(\s+show)?$/.test(label) || /\bvt\s+show\b/.test(label)) return true;
  if (/^vt\b/.test(label) && /\b(show|track|break|id|open|close|billing)\b/.test(label)) return true;
  if (/voice\s*tracks?/.test(label)) return true;
  if (/\b(billing|billboard|underwriting|underwriter)\b/.test(label)) return true;
  return false;
}

function sanitizeNowPlayingText(text: unknown, artist?: unknown, title?: unknown): string | null {
  const raw = asTrimmedText(text);
  if (!raw || raw === 'Station Offline') return null;
  if (
    isHiddenProductionLabel(raw) ||
    isHiddenProductionLabel(asTrimmedText(artist)) ||
    isHiddenProductionLabel(asTrimmedText(title)) ||
    isHiddenProductionLabel(`${asTrimmedText(artist)} ${asTrimmedText(title)}`)
  ) {
    return LIVE_BREAK_LABEL;
  }
  return raw;
}

function asMount(value: unknown): { title?: unknown; yp_currently_playing?: unknown; listenurl?: unknown } | null {
  if (!value || typeof value !== 'object') return null;
  return value as { title?: unknown; yp_currently_playing?: unknown; listenurl?: unknown };
}

function icecastMount(payload: unknown): {
  title?: unknown;
  yp_currently_playing?: unknown;
  listenurl?: unknown;
} | null {
  const source =
    payload && typeof payload === 'object' && 'icestats' in payload
      ? (payload as { icestats?: { source?: unknown } }).icestats?.source
      : undefined;
  if (!source) return null;
  const mounts = (Array.isArray(source) ? source : [source]).map(asMount).filter(Boolean);
  return (
    mounts.find((mount) => String(mount?.listenurl || '').includes('/radio.mp3')) ||
    mounts[0] ||
    null
  );
}

function textFromIcecast(payload: unknown): string | null {
  const mount = icecastMount(payload);
  const title = asTrimmedText(mount?.yp_currently_playing) || asTrimmedText(mount?.title);
  return sanitizeNowPlayingText(title);
}

async function jsonOrNull(response: Response): Promise<unknown | null> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function textFromAzuraCast(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const data = payload as {
    is_online?: unknown;
    now_playing?: { song?: { text?: unknown; artist?: unknown; title?: unknown } };
  };
  if (data.is_online === false) return null;
  const song = data.now_playing?.song;
  return sanitizeNowPlayingText(song?.text, song?.artist, song?.title);
}

export function AudioProvider({ children }: { children: ReactNode }) {
  // Source and playback state
  const [sourceType, setSourceType] = useState<AudioSourceType>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.85);
  const [isMuted, setIsMuted] = useState(false);
  
  // Stream state
  const [streamUrl, setStreamUrl] = useState<string | null>(DEFAULT_STREAM_URL);
  const [nowPlaying, setNowPlaying] = useState<RadioShow | null>(null);
  const [isLive, setIsLive] = useState(false);
  const [streamNowPlayingText, setStreamNowPlayingText] = useState<string | null>(null);
  const [streamTrack, setStreamTrack] = useState<{
    title: string;
    artist: string;
    artwork: string;
    artUrl: string;
    album: string;
    duration: number;
    isOnline: boolean;
    nextText: string;
    stationId: number;
    shortcode: string;
    stationName: string;
    listenUrl: string;
    text: string;
    elapsed: number;
    fetchedAt: number;
    listeners: number;
  } | null>(null);
  
  // Podcast state
  const [currentEpisode, setCurrentEpisode] = useState<PodcastQueueItem | null>(null);
  const [podcastQueue, setPodcastQueue] = useState<PodcastQueueItem[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Audio element ref
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const wasPlayingRef = useRef(false);
  const currentEpisodeRef = useRef<PodcastQueueItem | null>(null);
  const podcastQueueRef = useRef<PodcastQueueItem[]>([]);
  const playPodcastRef = useRef<(
    audioUrl: string,
    title: string,
    options?: PodcastPlayOptions
  ) => Promise<void> | void>(() => undefined);
  
  // Load volume from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
      if (saved) {
        const parsed = parseFloat(saved);
        if (!isNaN(parsed) && parsed >= 0 && parsed <= 1) {
          setVolumeState(parsed);
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);
  
  // Save volume to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString());
    } catch {
      // Ignore localStorage errors
    }
  }, [volume]);
  
  // Initialize audio element (run once on mount)
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'none';
    audio.setAttribute('playsinline', 'true');
    audioRef.current = audio;
    
    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
    };
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      const queue = podcastQueueRef.current;
      const current = currentEpisodeRef.current;
      if (!queue.length || !current) return;
      const idx = queue.findIndex((item) => item.audioUrl === current.audioUrl);
      if (idx >= 0 && idx < queue.length - 1) {
        const next = queue[idx + 1];
        void playPodcastRef.current(next.audioUrl, next.title, {
          imageUrl: next.imageUrl,
          queue,
        });
      }
    };
    const handleError = () => {
      setIsPlaying(false);
      setIsLoading(false);
      setError('Audio playback failed. Please try again.');
    };
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const handleLoadedMetadata = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlay);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlay);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.pause();
    };
  }, []);
  
  // Update volume
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);
  
  // Load stream URL on mount
  useEffect(() => {
    const loadStreamUrl = async () => {
      try {
        const { publicApi } = await import('@/lib/api');
        const response = await publicApi.settings.get();
        const url = response.data.streamUrl;
        setStreamUrl(url && url.trim() ? url.trim() : DEFAULT_STREAM_URL);
      } catch (error) {
        console.error('Failed to load stream URL:', error);
        setStreamUrl(DEFAULT_STREAM_URL);
      }
    };
    loadStreamUrl();
  }, []);
  
  // Title comes from Icecast mount metadata (what is actually on the stream).
  useEffect(() => {
    let cancelled = false;

    const fetchLineup = async () => {
      try {
        const { publicApi } = await import('@/lib/api');
        const [lineupRes, icecastRes, azuraRes] = await Promise.allSettled([
          publicApi.schedule.getLineup(),
          fetch(ICECAST_STATUS_URL, { cache: 'no-store' }),
          fetch(RADIO_NOW_PLAYING_URL, { cache: 'no-store' }),
        ]);

        if (cancelled) return;

        if (lineupRes.status === 'fulfilled') {
          setNowPlaying(lineupRes.value.data.current);
          setIsLive(!!lineupRes.value.data.current);
        } else {
          setNowPlaying(null);
          setIsLive(false);
        }

        let icecastText: string | null = null;
        if (icecastRes.status === 'fulfilled' && icecastRes.value.ok) {
          icecastText = textFromIcecast(await jsonOrNull(icecastRes.value));
        }

        let azuraText: string | null = null;
        if (azuraRes.status === 'fulfilled' && azuraRes.value.ok) {
          azuraText = textFromAzuraCast(await jsonOrNull(azuraRes.value));
        }

        const currentText = icecastText || azuraText;
        if (currentText) {
          setStreamNowPlayingText(currentText);
        }
      } catch (error) {
        console.error('Failed to fetch lineup:', error);
      }
    };

    fetchLineup();
    const interval = setInterval(fetchLineup, NOW_PLAYING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);
  
  // Play stream
  const playStream = useCallback(async () => {
    const audio = audioRef.current;
    const url = (streamUrl && streamUrl.trim()) || DEFAULT_STREAM_URL;
    if (!audio) return;
    if (isMuted) {
      setIsMuted(false);
    }
    if (volume <= 0.001) {
      setVolumeState(0.85);
    }
    audio.muted = false;
    setError(null);

    if (sourceType === 'stream' && !audio.paused) return;

    audio.pause();
    audio.preload = 'none';
    // Icecast drops the connection on pause; reattach so the first click always starts audio.
    audio.src = url;

    if (sourceType !== 'stream') {
      setSourceType('stream');
      currentEpisodeRef.current = null;
      podcastQueueRef.current = [];
      setCurrentEpisode(null);
      setPodcastQueue([]);
      setCurrentTime(0);
      setDuration(0);
    }

    try {
      await playNow(audio);
    } catch (err) {
      console.error('Failed to play stream:', err);
      setError('Click play to start the stream');
    }
  }, [sourceType, streamUrl, isMuted, volume]);
  
  // Play podcast
  const playPodcast = useCallback(async (
    audioUrl: string,
    title: string,
    options?: PodcastPlayOptions
  ) => {
    const audio = audioRef.current;
    if (!audio) return;
    // User explicitly clicked play; ensure we are not silently muted.
    if (isMuted) {
      setIsMuted(false);
    }
    if (volume <= 0.001) {
      setVolumeState(0.85);
    }
    audio.muted = false;

    setError(null);
    if (options?.queue) {
      podcastQueueRef.current = options.queue;
      setPodcastQueue(options.queue);
    }

    const episode: PodcastQueueItem = {
      audioUrl,
      title,
      imageUrl: options?.imageUrl,
    };
    
    // If same episode, toggle play/pause
    if (currentEpisode?.audioUrl === audioUrl) {
      if (audio.paused) {
        try {
          await playNow(audio);
        } catch (err) {
          setError('Click play to resume');
        }
      } else {
        audio.pause();
      }
      return;
    }
    
    // Switch to new episode
    audio.pause();
    audio.preload = 'none';
    audio.src = audioUrl;
    
    setSourceType('podcast');
    currentEpisodeRef.current = episode;
    setCurrentEpisode(episode);
    setCurrentTime(0);
    setDuration(0);
    wasPlayingRef.current = true;
    
    try {
      await playNow(audio);
    } catch (err) {
      console.error('Failed to play podcast:', err);
      setError('Click play to start the episode');
    }
  }, [currentEpisode, isMuted, volume]);

  playPodcastRef.current = playPodcast;
  
  // Pause
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
  }, []);
  
  // Toggle play/pause
  const toggle = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (audio.paused) {
      if (sourceType === 'podcast') {
        try {
          await playNow(audio);
        } catch (err) {
          setError('Click play to resume');
        }
      } else {
        await playStream();
      }
    } else {
      audio.pause();
    }
  }, [sourceType, streamUrl, playStream]);
  
  // Set volume
  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    if (clamped > 0 && isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);
  
  // Toggle mute
  const toggleMute = useCallback(() => {
    setIsMuted(m => !m);
  }, []);
  
  // Seek (podcast only)
  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio || sourceType !== 'podcast') return;
    
    const clamped = Math.max(0, Math.min(duration || 0, time));
    audio.currentTime = clamped;
    setCurrentTime(clamped);
  }, [sourceType, duration]);

  const skipBy = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio || sourceType !== 'podcast') return;
    const next = (audio.currentTime || 0) + seconds;
    const clamped = Math.max(0, Math.min(duration || audio.duration || next, next));
    audio.currentTime = clamped;
    setCurrentTime(clamped);
  }, [sourceType, duration]);

  const playQueuedOffset = useCallback((offset: number) => {
    const current = currentEpisodeRef.current;
    const queue = podcastQueueRef.current;
    if (!current || !queue.length) return;
    const idx = queue.findIndex((item) => item.audioUrl === current.audioUrl);
    const next = queue[idx + offset];
    if (!next) return;
    void playPodcast(next.audioUrl, next.title, { imageUrl: next.imageUrl, queue });
  }, [playPodcast]);

  const playNextEpisode = useCallback(() => playQueuedOffset(1), [playQueuedOffset]);
  const playPreviousEpisode = useCallback(() => playQueuedOffset(-1), [playQueuedOffset]);

  const currentIndex = currentEpisode
    ? podcastQueue.findIndex((item) => item.audioUrl === currentEpisode.audioUrl)
    : -1;
  const hasPreviousEpisode = currentIndex > 0;
  const hasNextEpisode = currentIndex >= 0 && currentIndex < podcastQueue.length - 1;

  
  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  const value: AudioContextState = {
    sourceType,
    isPlaying,
    volume,
    isMuted,
    streamUrl,
    nowPlaying,
    isLive,
    streamNowPlayingText,
    streamTrack,
    currentEpisode,
    currentTime,
    duration,
    isLoading,
    error,
    hasPreviousEpisode,
    hasNextEpisode,
    playStream,
    playPodcast,
    pause,
    toggle,
    setVolume,
    toggleMute,
    seek,
    skipBy,
    playNextEpisode,
    playPreviousEpisode,
    clearError,
    setStreamTrack,
  };
  
  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
}
